import React from 'react';
import PropTypes from 'prop-types';
import { Markup } from 'interweave';
import I18n from 'utils/I18n';
import { Button, LinkButton } from '@strava/ui/Button';
import AvatarAndMeta from '../AvatarAndMeta';
import { buildTimeAgoTimestamp } from '../../utils/buildTimeAgoTimeStamp';
import { DELETE_COMMENT_START } from '../kudosAndComments.actions';
import Microfrontend, {
  getMfeRemoteEntryUrl
} from '../../../../apps/Microfrontend';
import { FEED_ENTRY_TYPES } from '../../../Feed/constants';
import styles from './styles.scss';

const prefix = 'templates.kudos_and_comments_modal';

const CommentItem = ({
  entry,
  entityId,
  entity,
  commentReactionAnalytics,
  shouldShowTrustMigratedReportingWeb,
  dispatch,
  observeCommentElement
}) => {
  const allowedCommentReactionEntities = [
    FEED_ENTRY_TYPES.activity,
    FEED_ENTRY_TYPES.groupActivity,
    FEED_ENTRY_TYPES.post
  ];

  const commentReactionCount =
    entry.reactions_summary && entry.reactions_summary.reaction_count
      ? entry.reactions_summary.reaction_count
      : 0;
  const hasReacted =
    entry.reactions_summary && entry.reactions_summary.has_reacted
      ? entry.reactions_summary.has_reacted
      : false;
  const timeAgoTimestamp = buildTimeAgoTimestamp(entry.timestamp);
  const showCommentReactions = allowedCommentReactionEntities.includes(entity);

  const deleteComment = (comment) => {
    if (!dispatch) {
      return;
    }

    dispatch({
      type: DELETE_COMMENT_START,
      payload: { comment, entityId, element: 'modal' }
    });
  };

  return (
    <div
      ref={(el) => {
        if (el && observeCommentElement) {
          observeCommentElement(el, entry.comment_id);
        }
      }}
      className={styles.entry}
      key={entry.comment_id}
      data-testid="entry"
      data-comment-id={entry.comment_id}
    >
      <AvatarAndMeta
        athleteMetaData={entry.athlete}
        rowContent={entry.comment}
        showCommentReactions={showCommentReactions}
        commentReactionCount={commentReactionCount}
        entryId={entry.comment_id}
        hasReacted={hasReacted}
        entityId={entityId}
        commentReactionAnalytics={commentReactionAnalytics}
      />
      <div className={styles.timestampAndCommentActions}>
        <div className={styles.timestamp}>
          <Markup noWrap={true} content={timeAgoTimestamp} />
        </div>
        {(entry.deletable || entry.reportable || entry.quarantinable) && (
          <div className={styles.commentActions}>
            {entry.reportable && (
              <>
                {shouldShowTrustMigratedReportingWeb ? (
                  <Microfrontend
                    url={getMfeRemoteEntryUrl('reporting')}
                    scope="strava_reporting"
                    component="./App"
                    appContext={{
                      entityId: entry.comment_id,
                      className: styles.reportCommentButton,
                      page: 'comment'
                    }}
                  />
                ) : (
                  <LinkButton
                    variant="text"
                    data-testid="report-comment-btn"
                    className={styles.actionLinkButton}
                    href={`/comments/${entry.comment_id}/feedback`}
                  >
                    {I18n.t(`${prefix}.report`)}
                  </LinkButton>
                )}
              </>
            )}
            {entry.deletable && (
              <Button
                type="button"
                className={styles.actionLinkButton}
                variant="text"
                data-testid="delete-comment-btn"
                onClick={() => deleteComment(entry)}
              >
                {I18n.t(`${prefix}.delete`)}
              </Button>
            )}
            {entry.quarantinable && (
              <LinkButton
                className={styles.actionLinkButton}
                variant="text"
                data-testid="quarantine-comment-btn"
                href={`/admin/quarantine/comment_form?comment_id=${entry.comment_id}`}
              >
                {I18n.t(`${prefix}.soft_delete`)}
              </LinkButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

CommentItem.propTypes = {
  entry: PropTypes.shape({
    comment_id: PropTypes.number.isRequired,
    deletable: PropTypes.bool.isRequired,
    reportable: PropTypes.bool.isRequired,
    quarantinable: PropTypes.bool.isRequired,
    athlete: PropTypes.shape({
      name: PropTypes.string.isRequired,
      avatar_url: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      member_type: PropTypes.string
    }).isRequired,
    comment: PropTypes.string,
    timestamp: PropTypes.string,
    reactions_summary: PropTypes.shape({
      reaction_count: PropTypes.number,
      has_reacted: PropTypes.bool
    })
  }).isRequired,
  entityId: PropTypes.string.isRequired,
  entity: PropTypes.string.isRequired,
  commentReactionAnalytics: PropTypes.shape({
    category: PropTypes.string.isRequired,
    page: PropTypes.string.isRequired,
    buttonElement: PropTypes.string.isRequired,
    listElement: PropTypes.string.isRequired
  }).isRequired,
  shouldShowTrustMigratedReportingWeb: PropTypes.bool,
  dispatch: PropTypes.func,
  observeCommentElement: PropTypes.func
};

export default CommentItem;
